import React, {useState, useMemo, useEffect, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  FormGrid,
  FullWidth,
  LoadingOverlay,
  SpinnerLabel,
  SpinnerRing,
} from '../../../../accountManagement/features/AccountInfo/AccountInfo.style'
import {DynamicField} from '../../../../accountManagement/features/AccountInfo/components/DynamicField'
import {
  buildFieldConfigMap,
  PARTNER_VALUES,
} from '../../../../accountManagement/features/AccountInfo/config/locationFieldRules'
import {
  validatePhoneNumber,
  validatePostalCode,
  fetchSelectedCountryRegionList,
} from '../../../../utils/address-validation-util'
import {
  selectRecurlyAccountInfo,
  selectSchedulerRequirementStatus,
  setRecurlyAccountInfo,
  updateProposalInfo,
} from '../../../../app-store/ProposalSlice'
import {
  Container,
  FooterText,
  GradientSection,
  NavBar,
  NavLeft,
  NavRight,
  NavForLabel,
  NavBusinessName,
  PageFooter,
  PageWrap,
} from '../ClientProposalView/styled.components'
import {Button} from '../../../../core/shared/Button'
import {BuildingIcon} from '../../../../accountManagement/shared/icons'
import {useNavigate, useParams} from 'react-router'
import {useProposalAuthClient} from './contexts/proposal-auth-context'
import {useEnsureProposalInfo} from '../../../../utils/client-proposal.util'
import {toast} from 'react-toastify'
import {
  AccountDetailsBuildingIcon,
  AccountDetailsWarning,
  CardInner,
  ContentWrapper,
  FormBody,
  FormCard,
  FormCardHeader,
  FormFooter,
  HeroArea,
  PageSubtitle,
  PageTitle,
  SectionBadge,
} from './account-information.styles'
import {
  PTArrowLeftSVG,
  PTArrowRightSVG,
  PTLogoWhite,
  PTKeapWhite,
} from '../../../../assets'
import {validateEmailWithApi} from '../../../../accountManagement/features/EmailLookup/EmailLookup.utils'

// // Normalize a network id to the networkid EMAIL form (e.g. govenm01 -> govenm01@thryv.com).
// // Pass through anything already email-shaped; return null for empty input.
// const toNetworkEmail = value => {
//   if (!value) return null
//   const v = String(value).trim()
//   if (!v) return null
//   return v.includes('@') ? v : `${v}@thryv.com`
// }

// Fixed rep-attribution identities for partner-channel roles — mirror of the backend
// resolveSalesRepIdentity constants in ooe-service/routes/multi-location-route.js.
const KEAP_PARTNER_SALES_REP_ID = 'fict997362@thryv.com'
const RESELLER_SALES_REP_ID = 'fict996814@thryv.com'

// KeapPartner/InfluencerOps/PartnerOps are all Keap partner-channel roles and attribute to
// the same fixed Keap partner rep id — mirror of the backend KEAP_PARTNER_CHANNEL_ROLES.
const KEAP_PARTNER_CHANNEL_ROLES = [
  'KeapPartner',
  'InfluencerOps',
  'PartnerOps',
]

// Client-side mirror of the backend resolveSalesRepIdentity (multi-location-route.js). The
// backend is authoritative — it recomputes both salesRepId and on_behalf_of server-side and
// strips any client-supplied onBehalf — so this is defense-in-depth only. Both salesRepId and
// onBehalf on the request body carry the same role-derived value, and a client-supplied
// selection is never honored for any role (dropped 2026-07-07, including for PremisePilot):
//   1. KeapPartner/InfluencerOps/PartnerOps → fixed Keap partner rep id
//   2. Reseller    → fixed reseller rep id
//   3. everything else (including PremisePilot) → the logged-in rep's networkid email
const resolveSalesRepIdentity = (role, currentUserNetworkEmail) => {
  const primaryRole = Array.isArray(role) ? role[0] : role
  if (KEAP_PARTNER_CHANNEL_ROLES.includes(primaryRole))
    return KEAP_PARTNER_SALES_REP_ID
  if (primaryRole === 'Reseller') return RESELLER_SALES_REP_ID
  return currentUserNetworkEmail || null
}

const FIELD_CONFIG = [
  {
    key: 'businessName',
    label: 'Business Name',
    placeholder: 'Business Name',
    type: 'text',
    layout: 'grid',
    validators: ['required'],
  },
  {
    key: 'clientFirstName',
    label: 'First Name',
    placeholder: 'First Name',
    type: 'text',
    layout: 'grid',
    validators: ['required', 'alphaName'],
  },
  {
    key: 'clientLastName',
    label: 'Last Name',
    placeholder: 'Last Name',
    type: 'text',
    layout: 'grid',
    validators: ['required', 'alphaName'],
  },
  {
    key: 'clientEmail',
    label: 'Email',
    placeholder: 'Email',
    type: 'email',
    layout: 'grid',
    disabled: true,
    validators: ['required', 'email'],
  },
  {
    key: 'telephone',
    label: 'Telephone',
    placeholder: '(###) ###-####',
    type: 'tel',
    layout: 'grid',
    maxLength: 18,
    validators: ['required', 'phone'],
  },
  {
    key: 'clientPhone',
    label: 'Mobile',
    placeholder: '(###) ###-####',
    type: 'tel',
    layout: 'grid',
    maxLength: 18,
    validators: ['required', 'phone'],
  },
  {
    key: 'abn',
    label: 'Australian Business Number (ABN)',
    placeholder: 'ABN',
    type: 'text',
    layout: 'full',
    validators: ['required', 'abn'],
    infoLink: 'https://abr.business.gov.au/',
    infoLinkLabel: 'ABN Lookup',
  },
  {
    key: 'nzbn',
    label: 'New Zealand Business Number (NZBN)',
    placeholder: 'NZBN',
    type: 'text',
    layout: 'full',
    validators: ['nzbn'],
    infoLink: 'https://www.nzbn.govt.nz/',
    infoLinkLabel: 'NZBN Lookup',
  },
  {
    key: 'address',
    label: 'Business Address',
    placeholder: 'Address',
    type: 'text',
    layout: 'full',
    section: 'address',
    validators: ['required'],
  },
  {
    key: 'city',
    label: 'City',
    placeholder: 'City',
    type: 'text',
    layout: 'grid',
    section: 'address',
    validators: ['required'],
  },
  {
    key: 'state',
    label: 'State',
    type: 'select',
    layout: 'grid',
    validators: ['required'],
  },
  {
    key: 'postCode',
    label: 'Postal Code',
    placeholder: '#####',
    type: 'text',
    layout: 'grid',
    section: 'address',
    validators: ['required', 'postalCode'],
  },
  {
    key: 'country',
    label: 'Country',
    type: 'select',
    layout: 'grid',
    disabled: true,
    validators: ['required'],
  },
]

// ── Validation ────────────────────────────────────────────────────────────────

function runFieldValidators(label, value, fieldValidators, countryCode = 'US') {
  const trimmedValue = typeof value === 'string' ? value.trim() : value

  if (fieldValidators.includes('required')) {
    if (
      trimmedValue === undefined ||
      trimmedValue === null ||
      trimmedValue === ''
    )
      return 'Required'
  }

  if (fieldValidators.includes('email') && trimmedValue) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue))
      return 'Invalid email address'
  }

  if (fieldValidators.includes('phone') && trimmedValue) {
    if (!validatePhoneNumber(countryCode, trimmedValue))
      return 'Invalid phone number'
  }

  if (fieldValidators.includes('postalCode') && trimmedValue) {
    if (!validatePostalCode(countryCode, trimmedValue))
      return 'Incorrect postal code format'
  }

  if (fieldValidators.includes('alphaName') && trimmedValue) {
    if (!/^[a-zA-Z\s'-]+$/.test(trimmedValue))
      return 'Name must contain only letters, spaces, hyphens, or apostrophes'
    if (trimmedValue.replace(/[^a-zA-Z]/g, '').length < 3)
      return `${label} must contain at least 3 letters`
  }

  if (fieldValidators.includes('abn') && trimmedValue) {
    if (trimmedValue.replace(/\D/g, '').length !== 11)
      return 'Please enter a valid, 11-digit ABN'
  }

  if (fieldValidators.includes('nzbn') && trimmedValue) {
    if (trimmedValue.replace(/\D/g, '').length !== 13)
      return 'Please enter a valid, 13-digit NZBN'
  }

  return null
}

function validateAllFields(fields, formData, countryCode) {
  const errors = {}
  fields.forEach(field => {
    const error = runFieldValidators(
      field.label,
      formData[field.key],
      field.validators ?? [],
      countryCode,
    )
    if (error) errors[field.key] = error
  })
  return errors
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ProposalAccountInformation = ({
  isExistingAccount,
  onValidityChange,
  variant = 'form',
  size = 'md',
}) => {
  const dispatch = useDispatch()
  const client = useProposalAuthClient()
  const {randomId} = useParams()
  const {proposalInfo} = useEnsureProposalInfo({proposalId: randomId, client})
  const selectedCountry = proposalInfo?.country ?? 'US'
  const recurlyAccountInfo = useSelector(selectRecurlyAccountInfo)
  const isScheduleRequired = useSelector(selectSchedulerRequirementStatus)

  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const navigate = useNavigate()

  // Dispatch default country ('US') on mount if not already set
  useEffect(() => {
    if (!proposalInfo?.country) dispatch(updateProposalInfo({country: 'US'}))
  }, [])

  // Pre-populate form fields from recurly account when it loads.
  // Only overwrite a field when Recurly supplies a non-empty value so that
  // values already saved to the proposal are never blanked out by a Recurly
  // account that is missing those details.
  useEffect(() => {
    if (Object.keys(recurlyAccountInfo).length === 0) return
    const recurlyFirstName = recurlyAccountInfo?.firstName || ''
    const recurlyLastName = recurlyAccountInfo?.lastName || ''
    const recurlyEmail = recurlyAccountInfo?.email || ''
    const recurlyPhone = recurlyAccountInfo?.address?.phone || ''
    const recurlyCountry = recurlyAccountInfo?.address?.country || ''
    dispatch(
      updateProposalInfo({
        ...proposalInfo,
        ...(recurlyFirstName && {clientFirstName: recurlyFirstName}),
        ...(recurlyLastName && {clientLastName: recurlyLastName}),
        ...(recurlyEmail && {clientEmail: recurlyEmail}),
        ...(recurlyPhone && {clientPhone: recurlyPhone}),
        ...((recurlyFirstName || recurlyLastName) && {
          businessName: `${recurlyFirstName} ${recurlyLastName}`.trim(),
        }),
        ...(recurlyCountry && {country: recurlyCountry}),
      }),
    )
  }, [recurlyAccountInfo])

  // ── Field config ──────────────────────────────────────────────────────────

  const stateOptions = useMemo(
    () => fetchSelectedCountryRegionList(selectedCountry),
    [selectedCountry],
  )

  const fieldConfigMap = useMemo(() => {
    const map = buildFieldConfigMap({
      account: proposalInfo,
      values: proposalInfo,
      selectedCountry,
      states: stateOptions,
    })
    // In the proposal flow, country must always be visible and editable
    map.country = {...map.country, visible: true, disabled: false}
    return map
  }, [proposalInfo, selectedCountry, stateOptions])

  const resolvedFields = useMemo(
    () =>
      FIELD_CONFIG.map(f => ({...f, ...(fieldConfigMap[f.key] || {})})).filter(
        f => f.visible !== false,
      ),
    [fieldConfigMap],
  )

  // ── Validity ──────────────────────────────────────────────────────────────

  const isFormValid = useMemo(() => {
    if (!selectedCountry) return false
    return (
      Object.keys(
        validateAllFields(resolvedFields, proposalInfo, selectedCountry),
      ).length === 0
    )
  }, [selectedCountry, proposalInfo, resolvedFields])

  useEffect(() => {
    onValidityChange?.(isFormValid)
  }, [isFormValid, onValidityChange])

  // ── Handlers ──────────────────────────────────────────────────────────────

  const updateFieldError = useCallback(
    (fieldKey, value) => {
      const field = resolvedFields.find(f => f.key === fieldKey)
      if (!field) return
      const error = runFieldValidators(
        field.label,
        value,
        field.validators ?? [],
        selectedCountry ?? 'US',
      )
      setErrors(prev => {
        const next = {...prev}
        if (error) next[fieldKey] = error
        else delete next[fieldKey]
        return next
      })
    },
    [resolvedFields, selectedCountry],
  )

  const handleChange = useCallback(
    (fieldKey, value) => {
      // Both the AU 'abn' field and the NZ 'nzbn' field store under the same
      // 'abn' key in the slice, matching the legacy single-key API shape.
      const storeKey = fieldKey === 'nzbn' ? 'abn' : fieldKey
      dispatch(updateProposalInfo({[storeKey]: value}))
      updateFieldError(fieldKey, value)
    },
    [dispatch, updateFieldError],
  )

  const handleBlur = useCallback(
    fieldKey => {
      setTouched(prev => ({...prev, [fieldKey]: true}))
      updateFieldError(fieldKey, proposalInfo[fieldKey])
    },
    [proposalInfo, updateFieldError],
  )

  const handleContinue = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    try {
      // If the account was already created (thryvId persisted to the proposal),
      // skip re-creation and go straight to the next step.
      if (proposalInfo.thryvId) {
        const {meta} = await validateEmailWithApi({
          email: proposalInfo.clientEmail,
          client,
          flowType: 'Proposal',
        })
        if (meta?.subDetails)
          dispatch(setRecurlyAccountInfo(meta.subDetails ?? {}))

        const nextPath = isScheduleRequired
          ? 'schedule-appointment'
          : 'client-payment'
        navigate(`/proposal-view/${proposalInfo.proposalId}/${nextPath}`)
        return
      }

      const locationPayload = {
        city: proposalInfo.city,
        state: proposalInfo.state,
        zipCode: proposalInfo.postCode,
        country: proposalInfo.country,
      }
      const {timeZone} = await client('accounts/validate/addressAndTZ', {
        data: locationPayload,
        method: 'POST',
      })
      let address = {
        ...locationPayload,
        street: proposalInfo.address,
        phone: proposalInfo.telephone,
      }
      const firstName = proposalInfo.clientFirstName ?? ''
      const lastName = proposalInfo.clientLastName ?? firstName

      const isPartner = PARTNER_VALUES.includes(proposalInfo.salesRepOrPartner)

      const parentPayload = {
        email: proposalInfo.clientEmail ?? '',
        firstName,
        lastName,
        company: proposalInfo.businessName ?? '',
        phone: proposalInfo.telephone ?? '',
        mobile: proposalInfo.clientPhone ?? '',
        address,
        timeZone,
        country: proposalInfo.country ?? 'US',
        auth0UserId: null,
        salesRepChannel: proposalInfo?.userRole ?? 'Admin',
        sfdcContactEmail: proposalInfo.salesRepId ?? '',
        salesRepOrPartner: proposalInfo.salesRepOrPartner || null,
        salesRepId: resolveSalesRepIdentity(
          proposalInfo?.userRole,
          proposalInfo.salesRepId,
        ),
        onBehalf: resolveSalesRepIdentity(
          proposalInfo?.userRole,
          proposalInfo.salesRepId,
        ),

        ...(isPartner && {
          xpCode: proposalInfo.xpCode,
        }),
      }

      const parent = await client('multi-location/parent', {
        data: parentPayload,
        method: 'POST',
      })

      const childPayload = {
        parentOrgId: parent.parentOrgId,
        parentSignupId: parent.signupId,
        parentCcId: parent.ccId,
        provisionRecordId: parent.provisionRecordId,
        email: proposalInfo.clientEmail,
        firstName,
        lastName,
        company: proposalInfo.businessName ?? '',
        phone: proposalInfo.clientPhone ?? '',
        country: proposalInfo.country ?? 'US',
        timeZone,
        address,
        auth0UserId: null,
        locationName: null,
        billTo: 'parent',
        salesRepChannel: proposalInfo?.userRole ?? 'Admin',
        sfdcContactEmail: proposalInfo.salesRepId ?? '',
        salesRepOrPartner: proposalInfo.salesRepOrPartner || null,

        // Inherit the parent's already-resolved rep-attribution identity; fall back to the
        // logged-in rep so the child never provisions with a blank salesRepId. onBehalf mirrors
        // salesRepId (both carry the same value; backend is authoritative).
        salesRepId: parentPayload.salesRepId || null,
        onBehalf: parentPayload.onBehalf || parentPayload.salesRepId || null,

        ...(isPartner && {
          xpCode: parentPayload.xpCode,
        }),
      }
      const location = await client('multi-location/location', {
        data: childPayload,
        method: 'POST',
      })

      const isAuOrNz = ['AU', 'NZ'].includes(proposalInfo.country)
      const abnValue = isAuOrNz
        ? (proposalInfo.abn ?? '').replaceAll(' ', '') || null
        : null
      await client(`proposals/${proposalInfo.proposalId}`, {
        data: {
          thryvId: location.thryvId,
          ...(abnValue && {abnNzbn: abnValue}),
        },
        method: 'PUT',
      })
      const verifyAccPayload = {
        thryvId: location.thryvId,
        ccId: parent.ccId,
        email: proposalInfo.clientEmail ?? '',
        firstName,
        lastName,
        company: proposalInfo.businessName ?? '',
        address,
      }
      await client('accounts/5/verify-account-created', {
        data: verifyAccPayload,
        method: 'PATCH',
      })

      dispatch(updateProposalInfo({thryvId: location.thryvId}))

      const {meta} = await validateEmailWithApi({
        email: proposalInfo.clientEmail,
        client,
        flowType: 'Proposal',
      })
      if (meta?.subDetails)
        dispatch(setRecurlyAccountInfo(meta.subDetails ?? {}))

      const nextPath = isScheduleRequired
        ? 'schedule-appointment'
        : 'client-payment'
      navigate(`/proposal-view/${proposalInfo.proposalId}/${nextPath}`)
    } catch (err) {
      toast.error(err.message || 'Failed to create account. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }, [isSaving, proposalInfo, client, navigate, isScheduleRequired])

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <PageWrap>
      {isSaving && (
        <LoadingOverlay>
          <SpinnerRing />
          <SpinnerLabel>
            {proposalInfo.thryvId ? 'Continuing…' : 'Creating account…'}
          </SpinnerLabel>
        </LoadingOverlay>
      )}
      <Container>
        <NavBar>
          <NavLeft>
            <img
              src={PTLogoWhite}
              alt="Thryv"
              style={{height: 30, objectFit: 'contain'}}
            />
            <span />
            <img
              src={PTKeapWhite}
              alt="Keap"
              style={{height: 30, objectFit: 'contain'}}
            />
          </NavLeft>
          {proposalInfo?.businessName && (
            <NavRight>
              <NavForLabel>Proposal for</NavForLabel>
              <NavBusinessName>{proposalInfo.businessName}</NavBusinessName>
            </NavRight>
          )}
        </NavBar>

        <GradientSection style={{flex: 1}}>
          <HeroArea>
            <PageTitle>Account Information</PageTitle>
            <PageSubtitle>
              Provide customer and business details to continue creating the
              order.
            </PageSubtitle>
          </HeroArea>
          <ContentWrapper>
            <CardInner>
              <FormCard>
                <FormCardHeader>
                  <SectionBadge>
                    <AccountDetailsBuildingIcon>
                      <BuildingIcon />
                    </AccountDetailsBuildingIcon>
                    Account Details
                  </SectionBadge>
                </FormCardHeader>
                <AccountDetailsWarning>
                  ⓘ Please ensure your business information is precise,
                  including Spelling, Punctuation and Abbreviations.
                </AccountDetailsWarning>
                <FormBody>
                  <FormGrid>
                    {resolvedFields.map(field => {
                      // nzbn display field shares the 'abn' slice key with the AU ABN field.
                      const valueKey = field.key === 'nzbn' ? 'abn' : field.key
                      const fieldElement = (
                        <DynamicField
                          key={field.key}
                          field={field}
                          value={proposalInfo[valueKey] ?? ''}
                          values={proposalInfo}
                          error={errors[field.key]}
                          touched={touched[field.key]}
                          disabled={
                            !!proposalInfo.thryvId ||
                            (field.key === 'country'
                              ? true
                              : !selectedCountry || field.disabled)
                          }
                          variant={variant}
                          size={size}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          country={selectedCountry}
                        />
                      )

                      return field.layout === 'full' ? (
                        <FullWidth key={field.key}>{fieldElement}</FullWidth>
                      ) : (
                        <React.Fragment key={field.key}>
                          {fieldElement}
                        </React.Fragment>
                      )
                    })}
                  </FormGrid>
                </FormBody>
              </FormCard>
              <FormFooter>
                <div style={{display: 'flex', gap: 1}}>
                  <Button
                    variant="secondary-gray"
                    type="button"
                    onClick={() =>
                      navigate(`/proposal-view/${proposalInfo.proposalId}`)
                    }
                    leftIcon={<PTArrowLeftSVG width="14" height="14" />}
                    size="md"
                    style={{borderRadius: '80px 0 0 80px'}}
                  >
                    Back
                  </Button>
                  <Button
                    variant="secondary-gray"
                    type="button"
                    onClick={() =>
                      navigate(`/proposal-view/${proposalInfo.proposalId}`)
                    }
                    size="md"
                    style={{borderRadius: '0 80px 80px 0'}}
                  >
                    Cancel
                  </Button>
                </div>
                <Button
                  variant="solid-gradiant"
                  size="md"
                  type="button"
                  disabled={(!proposalInfo.thryvId && !isFormValid) || isSaving}
                  onClick={handleContinue}
                  style={{
                    borderRadius: '100px',
                    letterSpacing: '0.6px',
                  }}
                >
                  Continue
                  <PTArrowRightSVG width="14" height="14" />
                </Button>
              </FormFooter>
            </CardInner>
          </ContentWrapper>

          <PageFooter>
            <FooterText>Copyright &copy; 2026 Thryv, Inc.</FooterText>
          </PageFooter>
        </GradientSection>
      </Container>
    </PageWrap>
  )
}
